/* Product-page review tiles (Nino, 27 Oct 2026). For every Judge.me "cards carousel" block on a
   product template (hair oil, cap, towel, brush, shower cap, the bundles):
   1. hides Judge.me's chevrons (assets/juva-pdp-reviews.css) and adds the homepage's progress
      line under the tiles, with round prev/next buttons beside it on desktop (≥750px) that
      click the hidden chevrons; on phones the line only (swipe moves the row);
   2. fills in what the cards widget does not render: the review title, date, the review-form
      answers (goal, concern and the recommend question as lines, hair type / age as chips) and the
      product thumbnail,
      from Judge.me's own carousel endpoint (`reviews_for_carousel`, the one the widget itself
      loads its cards from; one call per product in the row, matched on `data-review-id`).
      Fails quiet: no data, plain tile. Judge.me's own tap-to-open lightbox stays as it is;
   3. puts the 5-star tiles first (Judge.me's cards carousel has no "highest rating" order, only
      most recent / media first): the card nodes are reordered in the row, recency kept within
      each group; `data-card-index` stays on each card, which is what the lightbox reads.
   Loaded by layout/theme.liquid on product templates only. */
(function () {
  var NL = /^nl/i.test(document.documentElement.lang || '');
  /* The answers keep Judge.me's English labels; only the label shown is translated. */
  var Q_TYPE = 'Hair type', Q_CONCERN = 'Hair concern', Q_GOAL = 'Hair goal', Q_AGE = 'Age',
      Q_RECOMMEND = 'Would I recommend it to a friend?';
  var T_TYPE = NL ? 'Haartype' : 'Hair type', T_AGE = NL ? 'Leeftijd' : 'Age',
      T_GOAL = NL ? 'Doel' : 'Goal', T_CONCERN = NL ? 'Zorg' : 'Concern',
      T_RECOMMENDS = NL ? 'Zou ik het een vriendin aanraden?' : 'Would I recommend it to a friend?';

  function decode(s) {
    /* Judge.me ships the answers HTML-escaped ("Shine &amp; frizz control"); parsed as an inert
       document (no scripts run), read back as text. */
    var doc = new DOMParser().parseFromString(String(s || ''), 'text/html');
    return (doc.body && doc.body.textContent || '').trim();
  }

  function initWidget(widget) {
    var content = widget.querySelector('.jdgm-content');
    var track = widget.querySelector('.jdgm-videos-container');
    if (!content || !track || !track.querySelector('.jdgm-card')) return false;
    if (widget.querySelector('.jrv-nav')) return true;
    sortCards(track);

    /* --- progress line --- */
    var nav = document.createElement('div');
    nav.className = 'jrv-nav';
    var chevron = function (d) {
      return '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="' + d + '"/></svg>';
    };
    nav.innerHTML =
      '<div class="jrv-progress" aria-hidden="true"><span class="jrv-progress__fill"></span></div>' +
      '<div class="jrv-arrows">' +
      '<button type="button" class="jrv-arrow" data-jrv-dir="-1" aria-label="Previous reviews">' + chevron('M15 18l-6-6 6-6') + '</button>' +
      '<button type="button" class="jrv-arrow" data-jrv-dir="1" aria-label="Next reviews">' + chevron('M9 18l6-6-6-6') + '</button>' +
      '</div>';
    content.parentNode.insertBefore(nav, content.nextSibling); /* below the tiles */
    nav.addEventListener('click', function (e) {
      var b = e.target.closest('[data-jrv-dir]');
      if (!b) return;
      var arrows = widget.querySelectorAll('.jdgm-arrow');
      var arrow = b.getAttribute('data-jrv-dir') === '-1' ? arrows[0] : arrows[arrows.length - 1];
      if (arrow) arrow.click(); /* Judge.me's own handler slides */
    });
    var fill = nav.querySelector('.jrv-progress__fill');
    var wrap = widget.querySelector('.jdgm-cards-wrapper');
    /* Put the row's offset on the active tile (Judge.me's maths are positional: clones count).
       Only called at known moments, never from an observer, so a touch drag is left alone. */
    function snap() {
      var all = track.querySelectorAll('.jdgm-card');
      var active = track.querySelector('.jdgm-card.active');
      var pos = Array.prototype.indexOf.call(all, active);
      if (pos < 0 || !all[0].offsetWidth) return;
      var step = all[0].offsetWidth + (parseFloat(getComputedStyle(track).gap) || 0);
      var want = -(pos * step);
      var m = (track.style.transform || '').match(/-?\d+(\.\d+)?/);
      var cur = m ? parseFloat(m[0]) : 0;
      if (Math.abs(cur - want) > 1) track.style.transform = 'translateX(' + want + 'px)';
    }
    function update() {
      if (!wrap) return;
      nav.style.maxWidth = wrap.offsetWidth + 'px'; /* as wide as the row of tiles */
      /* one full-width tile when the widget itself is narrow (phones, the PDP details column) */
      var narrow = content.clientWidth < 640;
      if (widget.classList.contains('jrv-narrow') !== narrow) {
        widget.classList.toggle('jrv-narrow', narrow);
        /* The tile just changed width under Judge.me's feet: its row offset was computed from
           the old 225px card, so the row showed a clone from the loop (a 4-star tile first, cut
           at the bottom). Ask Judge.me to re-measure, then put the row on the active tile
           ourselves in case it did not (Safari). */
        setTimeout(function () { window.dispatchEvent(new Event('resize')); snap(); }, 0);
        setTimeout(snap, 400);
      }
      var cards = track.querySelectorAll('.jdgm-card:not(.jdgm-clone-before):not(.jdgm-clone-after)');
      var active = track.querySelector('.jdgm-card.active');
      var n = cards.length;
      if (!n || !active) return;
      /* Narrow rows show one tile: the row follows the active tile's height, so the line sits
         right under it instead of under the tallest review in the set. Written only when it
         differs, so the style observer below cannot loop. */
      var h = narrow ? active.offsetHeight + 'px' : '';
      if (wrap.style.height !== h) wrap.style.height = h;
      var idx = Array.prototype.indexOf.call(cards, active);
      if (idx < 0) idx = parseInt(active.getAttribute('data-card-index'), 10) || 0;
      var step = cards[0].offsetWidth + (parseFloat(getComputedStyle(track).gap) || 0);
      var view = Math.max(1, Math.round(content.clientWidth / step));
      var w = Math.min(Math.max(view / n, 0.08), 1);
      var left = Math.min(Math.max(idx / n, 0), 1 - w);
      fill.style.width = (w * 100) + '%';
      fill.style.left = (left * 100) + '%';
    }
    new MutationObserver(update).observe(track, { attributes: true, subtree: true, attributeFilter: ['class', 'style'] });
    /* Judge.me's own resize handler writes the wrapper height too (from cards measured before
       the title / answers landed, which clipped the tile's bottom on phones): re-assert ours
       whenever the wrapper's style changes or any tile changes size. */
    new MutationObserver(update).observe(wrap, { attributes: true, attributeFilter: ['style'] });
    var ro = new ResizeObserver(update);
    function observeCards() { track.querySelectorAll('.jdgm-card').forEach(function (c) { ro.observe(c); }); }
    observeCards();
    new MutationObserver(observeCards).observe(track, { childList: true });
    window.addEventListener('resize', update);
    update();
    loadDetails(track, update);
    return true;
  }

  /* --- 5-star tiles first (stable: recency kept within each group) ---
     Judge.me loops the row with clones of the last cards before it (.jdgm-clone-before) and of
     the first cards after it (.jdgm-clone-after); its slide maths are positional, so the clone
     counts must stay. Only the originals are reordered; the clones are rebuilt around them. */
  function sortCards(track) {
    var all = Array.prototype.slice.call(track.querySelectorAll('.jdgm-card'));
    var isBefore = function (c) { return c.classList.contains('jdgm-clone-before'); };
    var isAfter = function (c) { return c.classList.contains('jdgm-clone-after'); };
    var orig = all.filter(function (c) { return !isBefore(c) && !isAfter(c); });
    if (orig.length < 2) return;
    var before = all.filter(isBefore), after = all.filter(isAfter);
    var stars = function (c) { return c.querySelectorAll('.jdgm-star.jdgm--on').length >= 5 ? 1 : 0; };
    var sorted = orig.slice().sort(function (x, y) { return stars(y) - stars(x); });
    var idx = function (c) { return c.getAttribute('data-card-index'); };
    var inOrder = sorted.every(function (c, i) { return c === orig[i]; });
    var clonesOk =
      before.every(function (c, i) { return idx(c) === idx(sorted[sorted.length - before.length + i]); }) &&
      after.every(function (c, i) { return idx(c) === idx(sorted[i]); });
    if (inOrder && clonesOk) return; /* already in order: no DOM churn, no observer loop */
    var activePos = all.indexOf(track.querySelector('.jdgm-card.active'));
    before.concat(after).forEach(function (c) { c.remove(); });
    sorted.forEach(function (c) { track.appendChild(c); });
    var i, k;
    for (i = sorted.length - before.length; i < sorted.length; i++) {
      k = sorted[i].cloneNode(true); k.classList.add('jdgm-clone-before'); track.insertBefore(k, sorted[0]);
    }
    for (i = 0; i < after.length; i++) {
      k = sorted[i].cloneNode(true); k.classList.add('jdgm-clone-after'); track.appendChild(k);
    }
    /* the active flag is positional too: put it back on the same slot */
    var now = Array.prototype.slice.call(track.querySelectorAll('.jdgm-card'));
    now.forEach(function (c) { c.classList.remove('active'); });
    if (activePos >= 0 && now[activePos]) now[activePos].classList.add('active');
  }

  /* --- title / date / answers / thumbnail from Judge.me's carousel endpoint --- */
  function loadDetails(track, onApplied, waited) {
    if (!window.jdgm || typeof window.jdgm.shopParams !== 'function') {
      /* the cards came from Judge.me's script, so jdgm is normally here already; give it 30 s */
      if ((waited || 0) < 150) setTimeout(function () { loadDetails(track, onApplied, (waited || 0) + 1); }, 200);
      return;
    }
    var base = (window.jdgm.CDN_API_HOST || 'https://cdn.judge.me/').replace(/\/?$/, '/') + 'reviews/reviews_for_carousel';
    var params = window.jdgm.shopParams();
    var byId = {}, thumbs = {};
    function apply() {
      track.querySelectorAll('.jdgm-card[data-review-id]').forEach(function (card) {
        var r = byId[card.getAttribute('data-review-id')];
        if (r) {
          if (r.title && !card.querySelector('.jrv-title')) {
            var el = document.createElement('div');
            el.className = 'jrv-title';
            el.textContent = r.title;
            card.insertBefore(el, card.firstChild);
          }
          if (r.date && !card.querySelector('.jrv-date')) {
            var dt = document.createElement('span');
            dt.className = 'jrv-date';
            dt.textContent = r.date;
            var info = card.querySelector('.jdgm-reviewer-info');
            if (info) info.appendChild(dt); else card.appendChild(dt); /* right of the name + tick */
          }
          if (r.cf && !card.querySelector('.jrv-meta')) {
            var meta = buildMeta(r.cf);
            if (meta) card.appendChild(meta);
          }
        }
        var link = card.querySelector('.jdgm-product-name a[href*="/products/"]');
        var pm = link && link.getAttribute('href').match(/\/products\/([^/?#]+)/);
        if (pm && thumbs[pm[1]] && !card.querySelector('.jrv-thumb')) {
          var img = document.createElement('img');
          img.className = 'jrv-thumb';
          img.alt = '';
          img.loading = 'lazy';
          img.src = thumbs[pm[1]];
          link.parentNode.insertBefore(img, link);
        }
      });
      if (typeof onApplied === 'function') onApplied(); /* the tiles just grew: re-measure the row */
    }
    function chip(label, value) {
      var c = document.createElement('span');
      c.className = 'jrv-chip';
      var k = document.createElement('span'); k.className = 'jrv-chip__k'; k.textContent = label + ' \u00b7 ';
      c.appendChild(k);
      c.appendChild(document.createTextNode(value));
      return c;
    }
    function line(label, value) {
      var l = document.createElement('div');
      l.className = 'jrv-line';
      var k = document.createElement('span'); k.className = 'jrv-line__k'; k.textContent = label + ' · ';
      l.appendChild(k);
      l.appendChild(document.createTextNode(value));
      l.title = value; /* the full answer on hover; the line is clipped to one row */
      return l;
    }
    function buildMeta(cf) {
      var meta = document.createElement('div');
      meta.className = 'jrv-meta';
      if (cf[Q_GOAL]) meta.appendChild(line(T_GOAL, cf[Q_GOAL]));
      if (cf[Q_CONCERN]) meta.appendChild(line(T_CONCERN, cf[Q_CONCERN]));
      if (cf[Q_RECOMMEND]) meta.appendChild(line(T_RECOMMENDS, cf[Q_RECOMMEND]));
      var chips = document.createElement('div');
      chips.className = 'jrv-chips';
      if (cf[Q_TYPE]) chips.appendChild(chip(T_TYPE, cf[Q_TYPE]));
      if (cf[Q_AGE]) chips.appendChild(chip(T_AGE, cf[Q_AGE]));
      if (chips.children.length) meta.appendChild(chips);
      return meta.children.length ? meta : null;
    }
    function load(pid) {
      var q = new URLSearchParams(params);
      q.set('carousel_type', 'cards');
      q.set('reviews_selection', 'current_product');
      q.set('product_ids', String(pid));
      q.set('display_order', 'most_recent');
      q.set('max_reviews', '30');
      q.set('star_rating', 'all');
      q.set('show_review_media', 'true');
      return fetch(base + '?' + q.toString()).then(function (r) { return r.json(); }).then(function (d) {
        (d && d.reviews || []).forEach(function (rev) {
          if (!rev || !rev.uuid) return;
          var cf = {};
          (Array.isArray(rev.cf_answers) ? rev.cf_answers : []).forEach(function (a) {
            var val = decode(a && a.value);
            if (a && a.question_title && val) cf[a.question_title] = val;
          });
          var iso = rev.created_at;
          byId[rev.uuid] = {
            title: decode(rev.title),
            date: iso && !isNaN(Date.parse(iso)) ? new Date(iso).toLocaleDateString(NL ? 'nl-NL' : 'en-US') : '',
            cf: cf
          };
        });
        apply();
      }).catch(function () {});
    }
    var handles = {};
    track.querySelectorAll('.jdgm-product-name a[href*="/products/"]').forEach(function (a) {
      var m = a.getAttribute('href').match(/\/products\/([^/?#]+)/);
      if (m) handles[m[1]] = true;
    });
    Object.keys(handles).forEach(function (h) {
      fetch('/products/' + h + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p || !p.id) return;
          if (p.featured_image) {
            thumbs[h] = p.featured_image + (p.featured_image.indexOf('?') > -1 ? '&' : '?') + 'width=112';
            apply();
          }
          return load(p.id);
        })
        .catch(function () {});
    });
    /* Judge.me rebuilds the row (resize, /nl/ translation); re-sort and re-apply when cards get
       replaced (the sort's own moves come back here too and find the row in order). */
    new MutationObserver(function () { sortCards(track); apply(); }).observe(track, { childList: true });
  }

  /* The star badge under the product photos ("★★★★★ 28 reviews", Judge.me's preview badge)
     scrolls to the review tiles. Delegated: the badge renders async. */
  document.addEventListener('click', function (e) {
    var badge = e.target.closest('.jdgm-prev-badge, .jdgm-preview-badge');
    if (!badge) return;
    var target = document.querySelector('.jdgm-cards-carousel');
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });

  var tries = 0;
  function init() {
    var widgets = document.querySelectorAll('.jdgm-cards-carousel');
    var pending = false;
    widgets.forEach(function (w) { if (!initWidget(w)) pending = true; });
    /* Judge.me renders async, up to ~30 s; a block with no reviews never gets cards. */
    if ((pending || !widgets.length) && tries++ < 150) setTimeout(init, 200);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
